// Navigation service to enable navigation outside of React components
import { NavigateFunction } from 'react-router-dom';

class NavigationService {
  private navigate: NavigateFunction | null = null;

  setNavigate(navigateFunction: NavigateFunction) {
    this.navigate = navigateFunction;
  }

  navigateTo(path: string) {
    if (this.navigate) {
      this.navigate(path);
    } else {
      console.warn('Navigate function not initialized, falling back to window.location');
      window.location.href = path;
    }
  }

  /**
   * Navigate to a path and replace the current entry in the history stack
   * This prevents the user from navigating back to the previous page
   * 
   * Use cases:
   * - After successful payment/checkout (prevent accidental duplicate bookings)
   * - After form submissions that shouldn't be revisited (prevent duplicate submissions)
   * - After authentication success (prevent going back to login/signup)
   * - After completing multi-step processes (prevent returning to intermediate steps)
   */
  navigateAndReplace(path: string) {
    if (this.navigate) {
      this.navigate(path, { replace: true });
    } else {
      console.warn('Navigate function not initialized, falling back to window.location.replace');
      window.location.replace(path);
    }
  }

  /**
   * Navigate to a path and clear the entire navigation stack
   * This removes all previous history entries
   * 
   * Use cases:
   * - After successful payment completion (prevent users from going back to checkout)
   * - After logout (clear all authenticated pages from history)
   * - After completing critical transactions (prevent navigation to sensitive forms)
   * 
   * Note: This uses replace: true and state to indicate stack should be cleared
   */
  navigateAndClearStack(path: string) {
    if (this.navigate) {
      // Replace current entry and add state to indicate fresh start
      this.navigate(path, { 
        replace: true,
        state: { clearStack: true }
      });
    } else {
      console.warn('Navigate function not initialized, falling back to window.location.replace');
      window.location.replace(path);
    }
  }
}

export const navigationService = new NavigationService();

